import { getFinancialYearId } from './accountingHelpers';

export function buildKendoDateRange(fromDate, toDate, extra = {}) {
  const from = fromDate instanceof Date ? fromDate : new Date(fromDate);
  const to = toDate instanceof Date ? toDate : new Date(toDate);
  return {
    FromDay: from.getDate(),
    FromMonth: from.getMonth() + 1,
    FromYear: from.getFullYear(),
    ToDay: to.getDate(),
    ToMonth: to.getMonth() + 1,
    ToYear: to.getFullYear(),
    ...extra,
  };
}

export function buildStatementDateRange(fromDate, toDate, extra = {}) {
  const from = fromDate instanceof Date ? fromDate : new Date(fromDate);
  const to = toDate instanceof Date ? toDate : new Date(toDate);
  return {
    DateDay: from.getDate(),
    DateMonth: from.getMonth() + 1,
    DateYear: from.getFullYear(),
    DateDay2: to.getDate(),
    DateMonth2: to.getMonth() + 1,
    DateYear2: to.getFullYear(),
    Level: 4,
    ...extra,
  };
}

export function buildProcessPdcPayload(row, reconcileDate, status, bankAccountId) {
  const date = reconcileDate instanceof Date ? reconcileDate : new Date(reconcileDate);
  return {
    Id: row.Id,
    Status: status,
    ReconcileBankAccountId: bankAccountId ? Number(bankAccountId) : row.ReconcileBankAccountId,
    FinancialYearId: getFinancialYearId(),
    DateDay: date.getDate(),
    DateMonth: date.getMonth() + 1,
    DateYear: date.getFullYear(),
  };
}

export function formatReportDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
}

export function indentByLevel(level) {
  const safe = Math.max(0, Number(level || 0) - 1);
  return { paddingLeft: `${safe * 1.25}rem` };
}

/** Running balance per account: cumulative (debit - credit) in date/id order. */
export function computeGeneralLedgerRunningBalances(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const sorted = [...rows].sort((a, b) => {
    const accountKeyA = String(a.Account?.AccountCode || a.Account?.Name || a.AccountId || '');
    const accountKeyB = String(b.Account?.AccountCode || b.Account?.Name || b.AccountId || '');
    if (accountKeyA !== accountKeyB) return accountKeyA.localeCompare(accountKeyB);

    const dateA = new Date(a.Date).getTime();
    const dateB = new Date(b.Date).getTime();
    if (dateA !== dateB) return dateA - dateB;

    return Number(a.Id || 0) - Number(b.Id || 0);
  });

  const runningByAccount = new Map();
  return sorted.map((row) => {
    const accountId = row.AccountId ?? row.Account?.Id ?? 0;
    const previous = runningByAccount.get(accountId) ?? 0;
    const debit = Number(row.Debit || 0);
    const credit = Number(row.Credit || 0);
    const balance = previous + debit - credit;
    runningByAccount.set(accountId, balance);
    return {
      ...row,
      RunningBalance: Math.round(balance * 100) / 100,
    };
  });
}

/** Group ledger rows by account with running balances and subtotals. */
export function groupGeneralLedgerByAccount(rows) {
  const withBalances = computeGeneralLedgerRunningBalances(rows);
  const groups = [];

  withBalances.forEach((row) => {
    const accountId = row.AccountId ?? row.Account?.Id ?? 0;
    let group = groups[groups.length - 1];
    if (!group || group.accountId !== accountId) {
      group = {
        accountId,
        accountCode: row.Account?.AccountCode || '',
        accountName: row.Account?.Name || 'Unknown account',
        rows: [],
        totalDebit: 0,
        totalCredit: 0,
        closingBalance: 0,
      };
      groups.push(group);
    }
    group.rows.push(row);
    group.totalDebit += Number(row.Debit || 0);
    group.totalCredit += Number(row.Credit || 0);
    group.closingBalance = row.RunningBalance;
  });

  return groups.map((group) => ({
    ...group,
    totalDebit: Math.round(group.totalDebit * 100) / 100,
    totalCredit: Math.round(group.totalCredit * 100) / 100,
    netBalance: Math.round(Math.abs(group.totalDebit - group.totalCredit) * 100) / 100,
  }));
}
